import React from 'react';
import { AxiosError, AxiosResponse } from 'axios';
import { AppBar, Button, Checkbox, CircularProgress, FormControlLabel, Grid, makeStyles, Typography, Link, Icon,
    Accordion, AccordionSummary, AccordionDetails } from '@material-ui/core';

import { BasicConfirmDialog } from '../../../shared/components/BasicConfirmDialog';
import { PageLocation } from '../../../shared/enum/PageLocation';
import { AuthContext } from '../../Authentication/AuthContext';
import { HomeContext } from '../../Home/HomeContext';
import { NewsStatus } from '../enum/NewsStatus';
import { ErrorStatus } from '../../../shared/enum/ErrorStatus';
import { EditedNewsFieldModel } from '../../../Models/EditedNewsFieldModel';
import { NewsModel } from '../../../Models/NewsModel';
import { VehicleDictionaryModel } from '../../../Models/VehicleDictionaryModel';
import { NewsContext } from '../NewsContext';
import NewsCategorization from '../components/NewsCategorization';
import NewsData from '../components/NewsData';
import EditedNewsFieldService from '../../../Services/EditedNewsFieldService';
import NewsService from '../../../Services/NewsService';
import VehicleDictionaryService from '../../../Services/VehicleDictionaryService';
import { JournalistModel } from '../../../Models/JournalistModel';
import { ClassificationModel, classificationModelInitial } from '../../../Models/ClassificationModel';
import ClassificationService from '../../../Services/ClassificationService';
import { RestoreClassification } from '../components/RestoreClassification';
import { ClassificationStatus } from '../enum/ClassificationStatus';
import { Alert } from '@material-ui/lab';
import { NewsLoadingContext } from '../NewsLoadingContext';
import { LoadingStatus } from '../enum/LoadingStatus';

export interface INewsClassificationProps {
    spreadsheetId: number,
    openList: boolean,
    setOpenList: (open: boolean) => void
}

const useStyles = makeStyles((theme) => ({
    root: {
        paddingTop: theme.spacing(3),
        paddingBottom: theme.spacing(3),
    },
    buttonsTop: {
        textTransform: 'initial',
        color: theme.palette.primary.main,
    },
    toolbar: {
        marginTop: '64px',
        padding: '8px',
        backgroundColor: '#F0F1F1',
    },
    loading: {
        minHeight: '80vh'
    },
    gridContainer: {
        marginTop: theme.spacing(3),
        maxWidth: '1800px',
        padding: theme.spacing(1)
    },
    gridContainerClient: {
        maxWidth: '1800px',
        padding: theme.spacing(3)
    },
    gridContainerText: {
        marginTop: '8px',
        maxWidth: '630px',
        padding: theme.spacing(1),
        height: 'calc(100vh - 100px)'
    },
    gridEditNews: {
        marginTop: theme.spacing(3)
    },
    text: {
        padding: theme.spacing(2)
    },
    clickHereText: {
        paddingBottom: theme.spacing(2)
    },
    clickHereLink: {
        textDecoration: 'underline',
        color: theme.palette.primary.dark,
        fontWeight: 'bold',
        '&:hover':{
            cursor: 'pointer'
        }
    }
}));

export default function NewsClassification({ spreadsheetId, openList, setOpenList }: INewsClassificationProps) {
    const classes = useStyles();

    const authContext = React.useContext(AuthContext);
    const homeContext = React.useContext(HomeContext);
    const newsContext = React.useContext(NewsContext);
    const newsLoadingContext = React.useContext(NewsLoadingContext);

    const [loading, setLoading] = React.useState<boolean>(false);    
    const [showConfirmExit, setShowConfirmExit] = React.useState<boolean>(false);

    const [copyClassification, setCopyClassification] = React.useState<ClassificationModel | null>(null);

    const [hasNotError, setHasNotError] = React.useState<boolean>(false);

    React.useEffect(() => {
        newsContext.setClassification(classificationModelInitial);

        fetchNews();
    }, [newsContext.newsCurrentId]);

    React.useEffect(() => {
        if (newsContext.errorDataStatus == ErrorStatus.HAS_NOT_ERROR && newsContext.errorCategorizationStatus == ErrorStatus.HAS_NOT_ERROR) {
            setHasNotError(true);
        }
        else if ((newsContext.errorDataStatus == ErrorStatus.HAS_ERROR ||
            newsContext.errorCategorizationStatus == ErrorStatus.HAS_ERROR) &&
            newsContext.errorAtLeastOneCategorizationStatus !== ErrorStatus.HAS_ERROR) {
            homeContext.showError('Ops.. existem campos com preenchimento inválido. Por favor, verifique os campos acima e tente novamente.');
        }
    }, [newsContext.errorDataStatus, newsContext.errorCategorizationStatus]);

    React.useEffect(() => {
        if (hasNotError == true) {
            saveClassification();
            setHasNotError(false);
        }
    }, [hasNotError]);

    const fetchAllNews = (callbackSuccess: () => void) => {
        if (spreadsheetId) {
            NewsService.newsService(spreadsheetId)
                .getAll()
                .then((res: AxiosResponse) => {
                    newsContext.setNewsList(res.data);
                    callbackSuccess();
                })
                .catch((error: AxiosError) => { homeContext.showError('', error); });
        }
    };

    const fetchNews = () => {
        if (newsContext.newsCurrentId) {
            setLoading(true);
            NewsService.newsService(spreadsheetId)
                .get(newsContext.newsCurrentId)
                .then((res: AxiosResponse) => {
                    const news : NewsModel = {...res.data};
                    newsContext.setNews(news);

                    const pendingOrClassifying = newsContext.newsList.filter(e => 
                        e.status == NewsStatus.PENDING || e.status == NewsStatus.CLASSIFYING
                    );

                    if (pendingOrClassifying.length > 0) {
                        if (news?.status == NewsStatus.CLASSIFIED
                            && news?.classificationId
                            && news?.classificationId > 0) {
                            fetchEditClassification(news?.classificationId);
                        } else {
                            if (newsContext.keepData)
                                copyNewsWithKeepData(news);
                            else {
                                const classification = newsToClassification(news);
                                classification.startClassification = new Date();
                                newsContext.setClassification(classification);
                            }
                        }
                    }
                })
                .catch((error: AxiosError) => homeContext.showError('', error))
                .finally(() => setLoading(false));
        }
    };

    const fetchEditClassification = (classificationId: number) => {
        newsLoadingContext.setClassificationLoading(LoadingStatus.LOADING);
        
        ClassificationService.classificationService()
            .get(classificationId)
            .then((res: AxiosResponse) => {
                if(res.data)
                    newsContext.setClassification(res.data);
                
                newsLoadingContext.setClassificationLoading(LoadingStatus.DONE);
            })
            .catch((error: AxiosError) => {
                homeContext.showError('', error);
                newsLoadingContext.setClassificationLoading(LoadingStatus.ERROR);
            });
    };

    const fetchNewsAfterRestore = () => {
        newsContext.setClassification(classificationModelInitial);

        fetchAllNews(() => {
            fetchNews();
        });
    };

    const newsToClassification = (news: NewsModel) => {
        const classification : ClassificationModel = {...classificationModelInitial};
        classification.newsId = news.id;
        classification.clientId = news.clientId;
        classification.publicationTitle = news.title;
        classification.publicationDate = news.publishDate;
        classification.clipperLink = news.clipperLink;
        classification.originalLink = news.newsLink;
        classification.vehicleId = news.vehicleId ? news.vehicleId : 0;
        classification.vehicleNomenclature = news.vehicle;
        classification.vehicleDictionary = news.vehicleOriginDictionary;
        classification.centimeter = news.centimeter;
        classification.startClassification = new Date();
        classification.userId = authContext.login.user.id;
        classification.responsible = authContext.login.user; 
        classification.clientCodeMetric = news.clientCodeMetric;      
        classification.mediaId = news.mediaId ? news.mediaId : 0; 

        classification.journalists = news.journalists;

        return classification;
    };

    const copyNewsWithKeepData = (news: NewsModel) => {
        const classification = newsToClassification(news);

        if (copyClassification) {
            classification.categories = copyClassification.categories;
            classification.journalists = copyClassification?.journalists;

            if (classification.mediaId == copyClassification.vehicle?.media?.id) {
                classification.expositions = copyClassification.expositions;
                classification.location = copyClassification.location;
                classification.period = copyClassification.period;
                classification.coverPresence = copyClassification.coverPresence;
            }
        }

        classification.startClassification = new Date();
        newsContext.setClassification(classification);        
    };

    const handleClickSave = () => {
        newsContext.setErrorDataStatus(ErrorStatus.RUN);
        newsContext.setErrorCategorizationStatus(ErrorStatus.RUN);
        newsContext.setErrorAtLeastOneCategorizationStatus(ErrorStatus.RUN);
    };

    const saveClassification = () => {
        homeContext.setOpenLoadingDialog(true);
        
        const classificationData = newsContext.classification;
        classificationData.userCodeMetric = authContext.login.user.codeMetric;
        classificationData.userId = authContext.login.user.id;
        classificationData.vehicle = newsContext.vehicle;
        classificationData.clientCodeMetric = newsContext.news.clientCodeMetric;

        if (newsContext.classification.id > 0) {
            ClassificationService.classificationService()
                .update(classificationData)
                .then(() => {
                    homeContext.setOpenLoadingDialog(false);
                    finishSaveAndCallNextNews();
                })
                .catch((error: AxiosError) => {
                    homeContext.setOpenLoadingDialog(false);
                    homeContext.showError('Erro ao atualizar classificação. <br/>', error);
                });
        } else {
            if (newsContext.vehicleMetricCorrect && !newsContext.classification.vehicleDictionary) {
                const vehicleDictionary: VehicleDictionaryModel = { 
                    id: 0, 
                    descriptionVehicle: newsContext.classification.vehicleNomenclature, 
                    clipperId: newsContext.news.clipperId, 
                    codeMediaMetric: newsContext.classification.vehicle?.media?.codeMetric ? newsContext.classification.vehicle?.media?.codeMetric : null, 
                    codeVehicleMetric: newsContext.classification.vehicle?.codeMetric ? newsContext.classification.vehicle?.codeMetric : null,
                    mediaId:  newsContext.vehicle.media ? newsContext.vehicle.media.id : null,
                    vehicleId: newsContext.vehicle.id
                };
                VehicleDictionaryService
                    .create(vehicleDictionary)
                    .then(() => { ''; })
                    .catch((error: AxiosError) => { homeContext.showError('', error); });
            }

            classificationData.endClassification = new Date();

            if (newsContext.keepData)             
                setCopyClassification(classificationData);

            ClassificationService.classificationService()
                .create(classificationData)
                .then((res: AxiosResponse) => {
                    homeContext.setOpenLoadingDialog(false);
                    finishSaveAndCallNextNews(res.data.object);
                })
                .catch((error: AxiosError) => {
                    homeContext.setOpenLoadingDialog(false);
                    homeContext.showError('Erro ao salvar classificação. <br/>', error);
                });
        }
    };
    
    const finishSaveAndCallNextNews = async (classification?: ClassificationModel) => {
        if (!(newsContext.classification.id > 0))
            await createAndSaveEditedNewsFields(newsContext.news, newsContext.classification);
        
        homeContext.setAlertProps({
            message: getAlertMessage(classification),
            severity: 'success',
            open: true
        });

        fetchAllNews(() => {
            if (newsContext.tabListSelected != 1)
                newsContext.setNewsIndex(newsContext.newsIndex + 1);
        });        
    };

    const getAlertMessage = (classification?: ClassificationModel) => {
        if (classification) {
            let idMessage = '';
            if (classification.metricId != '')
                idMessage = `ID Metric: ${classification.metricId}`;
            else
                idMessage = `ID Plataforma: ${classification.id}`;
            
            return (
                <>
                    <Typography variant="caption">
                        A classificação da notícia foi salva com sucesso! <br/>
                        <strong>{idMessage}</strong>
                    </Typography>
                </>
            );
        }
        else
            return 'A classificação da notícia foi alterada com sucesso!';
    };
    
    const createAndSaveEditedNewsFields = async (initialStateOfNews: NewsModel, currentStateOfClassification: ClassificationModel) => {

        const editedNewsFields: EditedNewsFieldModel[] = [];

        createEditedNewsField(editedNewsFields, initialStateOfNews.title, currentStateOfClassification.publicationTitle, 'Título');
        createEditedNewsField(editedNewsFields, initialStateOfNews.clipperLink, currentStateOfClassification.clipperLink, 'Link da Clipadora');
        createEditedNewsField(editedNewsFields, initialStateOfNews.newsLink, currentStateOfClassification.originalLink, 'Link Externo');
        createEditedNewsField(editedNewsFields, initialStateOfNews.publishDate, currentStateOfClassification.publicationDate, 'Data da Publicação');
        createEditedNewsField(editedNewsFields, initialStateOfNews.vehicleCodeMetric, currentStateOfClassification.vehicle?.codeMetric, 'Veículo da Notícia');
        createEditedNewsField(editedNewsFields, initialStateOfNews.centimeter, currentStateOfClassification.centimeter, 'Centimetragem');
        createEditedNewsFieldJournalist(editedNewsFields, initialStateOfNews.journalistSpreadsheetName ? initialStateOfNews.journalistSpreadsheetName: '', currentStateOfClassification.journalists, 'Jornalistas');

        saveEditedNewsFields(editedNewsFields);
    };
    
    const createEditedNewsField = (editedNewsFields: EditedNewsFieldModel[], value1: any, value2: any, fieldName: string) => {
        if (value1 !== value2) {
            editedNewsFields.push({
                id: 0,
                newsId: newsContext.news.id,
                userId: authContext.login.user.id,
                fieldName: fieldName,
                oldValue: (value1 != null ? value1.toString() : ''),
                newValue: (value2 != null ? value2.toString() : value2)
            });
        }
    };
    
    const createEditedNewsFieldJournalist = (editedNewsFields: EditedNewsFieldModel[], spreadsheetJournalist: string, currentJournalists: JournalistModel[], fieldName: string) => {

        const currentJournalistListContainsJournalist = (spreadsheetJournalist: string, currentJournalists: JournalistModel[]): boolean => {

            let found = false;

            for (let i = 0; i < currentJournalists.length; ++i) {
                if (spreadsheetJournalist && (spreadsheetJournalist.toLowerCase() === currentJournalists[i].name.toLowerCase())) {
                    found = true;
                    break;
                }
            }

            return found;
        };

        if (!currentJournalistListContainsJournalist(spreadsheetJournalist, currentJournalists)) {
            editedNewsFields.push({
                id: 0,
                newsId: newsContext.news.id,
                userId: authContext.login.user.id,
                fieldName: fieldName,
                oldValue: spreadsheetJournalist ? spreadsheetJournalist : '',
                newValue: currentJournalists.map(obj => obj.name).join(',')
            });
        }
    };
    
    const saveEditedNewsFields = (editedNewsFields: EditedNewsFieldModel[]) => {
        editedNewsFields.forEach((editedNewsField: EditedNewsFieldModel) => {
            EditedNewsFieldService.editedNewsFieldService(newsContext.news.id)
                .create(editedNewsField)
                .then(() => { ''; })
                .catch((error: AxiosError) => { homeContext.showError('', error); });
        });
    };

    const handleNewsToNotClassify = () => {
        if (spreadsheetId && newsContext.news.id) {
            setLoading(true);
            NewsService.updateStatusNews(spreadsheetId, newsContext.news.id, NewsStatus.NOT_CLASSIFY)
                .then(() => {
                    fetchAllNews(() => {
                        if (newsContext.tabListSelected != 1)
                            newsContext.setNewsIndex(newsContext.newsIndex + 1);
                    });
                })
                .catch((error: AxiosError) => { homeContext.showError('', error); })
                .finally(() => { setLoading(false); });
        }
    };

    const handleNewsToPending = () => {
        if (spreadsheetId && newsContext.news.id) {
            NewsService.updateStatusNews(spreadsheetId, newsContext.news.id, NewsStatus.PENDING)
                .then(() => {
                    fetchAllNews(() => { fetchNews(); });                    
                })
                .catch((error: AxiosError) => { homeContext.showError('', error); });
        }
    };

    const getNewsClassifiedToEdit = () => {
        return (            
            <>
                {!loading && newsContext.listNewsPending.length > 0 
                          && newsContext.news.id > 0 
                          && newsContext.newsCurrentId 
                          && newsContext.newsCurrentId > 0
                          && newsContext.news.status == NewsStatus.CLASSIFIED 
                          && getNewsToClassify()
                }
            </>
        );
    };

    const getMessageNewsNotClassify = () => {
        return (            
            <>
                {!loading && newsContext.listNewsPending.length > 0 
                          && newsContext.news.id > 0
                          && newsContext.newsCurrentId 
                          && newsContext.newsCurrentId > 0
                          && newsContext.news.status == NewsStatus.NOT_CLASSIFY 
                          &&
                    <Grid
                        container
                        justify="center">
                        <Grid
                            container
                            spacing={4}
                            alignItems='center'
                            direction='row'
                            className={classes.gridContainerText}>
                            <Grid item xs={12} >
                                <Typography variant="h5" align="center" className={classes.text}>
                                    Esta notícia foi marcada por você como descartada.
                                </Typography>
                                <Typography variant="h6" align="center" className={classes.text}>
                                    Caso tenha mudado de ideia e queira classificá-la, basta clicar no botão abaixo.
                                </Typography>
                                <Grid container justify="center">
                                    <Button variant="outlined" onClick={handleNewsToPending}>
                                        Classificar notícia
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                }
            </>
        );
    };

    const getMessageNotNews = () => {
        return (            
            <>
                {!loading && newsContext.listNewsPending.length > 0 && !newsContext.newsCurrentId &&
                    <Grid
                        container
                        justify="center">
                        <Grid
                            container
                            spacing={4}
                            alignItems='center'
                            direction='row'
                            className={classes.gridContainerText}>
                            <Grid item xs={12}>
                                <Typography variant="h5" align="center" className={classes.text}>
                                    Clique em uma notícia no menu lateral esquerdo para classificá-la.
                                </Typography>
                                <Typography variant="h6" align="center" className={classes.text}>
                                    Os campos para classificação aparecerão aqui.
                                </Typography>
                            </Grid>
                        </Grid>
                    </Grid>
                }
            </>
        );
    };

    const getClassificationNewsPending = () => {
        return (            
            <>
                {!loading && newsContext.listNewsPending.length > 0 
                          && newsContext.news.id > 0 
                          && newsContext.newsCurrentId 
                          && newsContext.newsCurrentId > 0
                          && newsContext.news.status == NewsStatus.PENDING 
                          &&
                 getNewsToClassify() 
                }  
            </>
        );
    };

    const getNewsToClassify = () => {
        return (
            <Grid
                container
                justify="center"
                alignItems="center">
                <Grid
                    container
                    justify="center"
                    className={classes.gridContainer}
                    spacing={2}>
                    <Grid item xs={12} >
                        <Typography variant="h3" >
                            Classificação das notícias
                        </Typography>
                    </Grid>
                    <Grid
                        container
                        justify="space-between"
                        className={classes.gridContainerClient}
                        spacing={2}>
                        <Typography variant="h4" color="textSecondary">
                            {newsContext.news.clientDescription}
                        </Typography>
                        { newsContext.classification && newsContext.news.classificationId && newsContext.news.classificationId > 0 
                            ? ( newsContext.news.classificationStatus == ClassificationStatus.REMOVED
                                ? (<RestoreClassification
                                    classificationStatus={newsContext.classification.status}
                                    onRestore={() => fetchNewsAfterRestore()}/>)
                                : (<Grid
                                    container
                                    direction="row"
                                    alignItems="center"
                                    className={classes.gridEditNews}>
                                    <Grid item>
                                        <Alert severity="success" icon={<Icon>check</Icon>}>Esta notícia já foi classificada!</Alert>
                                    </Grid> 
                                </Grid>)
                            )
                            : (<Button 
                                variant='outlined' 
                                color='primary' 
                                size='large'
                                onClick={handleNewsToNotClassify}>
                                    Não classificar
                            </Button>)
                        }
                    </Grid>
                    <Grid
                        container
                        spacing={3}
                        direction="column"
                        alignItems="stretch"
                        justify="center"
                        className={classes.root}>
                        <Accordion defaultExpanded>
                            <AccordionSummary expandIcon={<Icon className={'material-icons-round'}>expand_more</Icon>}>
                                <Typography variant="h5">Dados da publicação</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <NewsData />
                            </AccordionDetails>
                        </Accordion>
                    </Grid>
                    <Grid
                        container
                        spacing={3}
                        direction="column"
                        alignItems="stretch"
                        justify="center"
                        className={classes.root}>
                        <Accordion defaultExpanded>
                            <AccordionSummary expandIcon={<Icon className={'material-icons-round'}>expand_more</Icon>}>
                                <Typography variant="h5">Categorização da publicação</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <NewsCategorization />
                            </AccordionDetails>
                        </Accordion>
                    </Grid>
                    <Grid item>
                        <Grid container 
                            spacing={1}
                            direction="column"
                            alignItems="center"
                            justify="center">
                            { !(newsContext.news.classificationId && newsContext.news.classificationId > 0) &&
                            <Grid item xs={12}>	
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={newsContext.keepData}
                                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => newsContext.setKeepData(event.target.checked)}
                                            name="checkKeepData"
                                            color="primary" />
                                    }
                                    label="Manter dados?" />
                            </Grid>
                            }
                            {newsContext.news.classificationStatus != ClassificationStatus.REMOVED && (
                                <Grid item xs={12}>	
                                    <Button 
                                        variant="contained" 
                                        color="primary" 
                                        onClick={handleClickSave} 
                                        size="large"
                                        fullWidth>
                                        { ( newsContext.news.classificationId
                                    && newsContext.news.classificationId > 0)
                                            ? 'SALVAR ALTERAÇÕES'
                                            : 'SALVAR CLASSIFICAÇÃO'
                                        }
                                    </Button>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        );
    };

    const clickHereLink = () => {
        return (
            <Link
                href="#"
                onClick={() => homeContext.setPageLocation(PageLocation.NEWS_METRIC_IDS)}
                className={classes.clickHereLink}>
                Clique aqui
            </Link>
        );
    };
    
    const getNotNewsPending = () => {
        return (
            <>
                {!loading && newsContext.listNewsPending.length == 0 &&
                    <Grid
                        container
                        justify="center">
                        <Grid
                            container
                            spacing={4}
                            alignItems='center'
                            direction='row'
                            className={classes.gridContainerText}>
                            <Grid item xs={12} >
                                <Typography variant="h5" align="center" className={classes.text}>
                                    Todas as notícias da planilha foram classificadas e salvas com sucesso!
                                </Typography>
                                <Typography variant="body1" align="center" className={classes.text}>
                                    Lembrando que consultas e edições devem ser realizadas no sistema Metric.
                                </Typography>
                                <Typography variant="body1" align="center" className={classes.clickHereText}>
                                    {clickHereLink()} para ver os IDs gerados.
                                </Typography>
                                <Grid container justify="space-around" >
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        size="large"
                                        onClick={() => homeContext.setPageLocation(PageLocation.NEWS_UPLOAD)}>
                                        Upload de nova planilha
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="large"
                                        onClick={() => homeContext.setPageLocation(PageLocation.HISTORY)}>
                                        histórico de planilha
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                }
            </>
        );
    };

    return (
        <>
            <AppBar position="fixed" className={classes.toolbar} color='inherit' elevation={0}>
                <Grid container justify={openList ? 'flex-end' : 'space-between'}>
                    {!openList && (
                        <Button
                            className={classes.buttonsTop}
                            variant="text"
                            startIcon={<Icon color="primary">chevron_right</Icon>}
                            onClick={() => setOpenList(true)}>
                        Exibir lista de notícias
                        </Button>)
                    }
                    <Button
                        className={classes.buttonsTop}
                        variant="text"                        
                        onClick={() => {
                            if (!loading && newsContext.news.id > 0 && newsContext.news.status == NewsStatus.PENDING)
                                setShowConfirmExit(true);
                            else
                                homeContext.setPageLocation(PageLocation.HISTORY);
                        }}>
                        Ir para o histórico de planilhas
                    </Button>
                </Grid>
            </AppBar>
            {loading && <Grid container
                direction="row"
                justify="center"
                alignItems="center"
                className={classes.loading}>
                <CircularProgress color="primary" size="124px" />
            </Grid>}
            { getClassificationNewsPending() }
            { getMessageNewsNotClassify() }
            { getNewsClassifiedToEdit() }
            { getMessageNotNews() }
            { getNotNewsPending() }
            <BasicConfirmDialog
                title="Sair sem salvar?"
                description="Ao sair sem salvar, você perderá todos os dados de classificação já preenchidos para esta publicação."
                handleFirstButton={() => homeContext.setPageLocation(PageLocation.HISTORY)}
                handleSecondButton={() => setShowConfirmExit(false)}
                textFirstButton="SAIR"
                textSecondButton="Permanecer na página"
                cssProperties={{
                    maxWidth: '550px'	
                }}
                open={showConfirmExit} />
        </>
    );
}